import {useEffect, useRef, useState} from 'react';
import {CheckmarkFilled} from '@carbon/icons-react';
import './ReportProgressBar.scss';

interface IReportProgressBarProps {
    current: number;
    total: number;
    onComplete?: () => void;
}

const ReportProgressBar = (props: IReportProgressBarProps) => {
    const {current, total, onComplete} = props;
    const [isCollapsing, setIsCollapsing] = useState(false);
    const [shouldRender, setShouldRender] = useState(true);
    const hasCompletedRef = useRef(false);
    const collapseTimerRef = useRef<NodeJS.Timeout | null>(null);
    const removeTimerRef = useRef<NodeJS.Timeout | null>(null);

    const percentage = total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
    const isComplete = current >= total && total > 0;

    useEffect(() => {
        if (isComplete && !hasCompletedRef.current) {
            hasCompletedRef.current = true;
            onComplete?.();

            // Wait 5 seconds before starting collapse
            collapseTimerRef.current = setTimeout(() => {
                setIsCollapsing(true);

                // Remove from DOM after collapse animation completes
                removeTimerRef.current = setTimeout(() => {
                    setShouldRender(false);
                }, 300);
            }, 5000);
        }

        // Cleanup timers on unmount
        return () => {
            if (collapseTimerRef.current) {
                clearTimeout(collapseTimerRef.current);
            }
            if (removeTimerRef.current) {
                clearTimeout(removeTimerRef.current);
            }
        };
    }, [isComplete, onComplete]);

    if (!shouldRender) {
        return null;
    }

    return (
        <div className={`report-progress-bar ${isCollapsing ? 'collapsing' : ''}`}>
            {!isComplete ? (
                <>
                    <div className="progress-bar-bg">
                        <div className="progress-bar-fill" style={{width: `${percentage}%`}} />
                    </div>
                    <div className="text-container">
                        <span>Loading report...</span>
                        <span>{percentage}%</span>
                    </div>
                    <div className="note">
                        Export will be available when loading completes.
                    </div>
                </>
            ) : (
                <div className="success-container">
                    <CheckmarkFilled size={20} />
                    <span>Report ready. Export is now available.</span>
                </div>
            )}
        </div>
    );
};

export default ReportProgressBar;
